/**
 * APPROVED INTENTS — the banner above the prompt box (EXPERT-ROOMS.md §14d, owner 2026-11-03). A change to a
 * customer's system runs only as a plan that carries the EXPERT's signature and the OWNER's consent, exactly as
 * approved, once. In the subscriber's room: each open plan with its signatures, Consent / Decline (the room's owner),
 * Run once approved (the room's run roles). In the expert's team room: the plans waiting for its signature — Sign /
 * Decline. Every signer can open the EXACT template first. R3: when the connection prepares the change first, the
 * signers see THE CHANGE (what the outside system will add / modify / replace) and can sign only once it's ready.
 * Confirms are the app's dialog (never the browser's).
 */
import React, { useState } from 'react';
import { CheckCircle2, Clock, FileCode2, Loader2 } from 'lucide-react';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const OPEN = ['pending', 'approved', 'deploying', 'starting'];

/** PURE — what the banner shows: this room's open plans + (an expert team's room) the plans waiting for it. */
export function openIntents(di) {
  return {
    room: (di?.room || []).filter((i) => OPEN.includes(i.status)),
    toSign: di?.toSign || [],
  };
}

/** PURE — may this plan be signed yet? (a connection that prepares the change first: only once it's ready) */
export function previewReady(it) {
  return !it?.previewNeeded || it?.preview?.status === 'ready';
}

export function targetLine(t) {
  return Object.entries(t || {}).map(([k, v]) => `${k} ${v}`).join(' · ');
}

function Sig({ role, it }) {
  const signed = (it.approvals || []).find((a) => a.role === role && a.decision === 'approve');
  const label = role === 'expert' ? 'Expert' : 'Owner';
  return signed
    ? <span className="inline-flex items-center gap-0.5 text-emerald-700" data-testid={`intent-sig-${role}-ok`}><CheckCircle2 className="h-3 w-3" aria-hidden />{label}</span>
    : <span className="inline-flex items-center gap-0.5 text-amber-700" data-testid={`intent-sig-${role}-waiting`}><Clock className="h-3 w-3" aria-hidden />{label}</span>;
}

export default function DeployIntentsBanner({ deployIntents, onAction, onReadPlan }) {
  const [busy, setBusy] = useState('');
  const [plan, setPlan] = useState({});            // intentId → template text (opened)
  const [err, setErr] = useState('');
  const { room, toSign } = openIntents(deployIntents);
  if (!room.length && !toSign.length) return null;
  const act = async (key, req, confirm) => {
    if (confirm && !(await confirmDialog(confirm))) return;
    setBusy(key); setErr('');
    try { await onAction?.(req); } catch (e) { setErr(e?.message || 'Something went wrong.'); } finally { setBusy(''); }
  };
  const read = async (it, req) => {
    if (plan[it.intentId] !== undefined) { setPlan((p) => { const n = { ...p }; delete n[it.intentId]; return n; }); return; }
    try { const r = await onReadPlan?.(req); setPlan((p) => ({ ...p, [it.intentId]: r?.artifact || '' })); } catch (e) { setErr(e?.message || 'Couldn\'t read the plan.'); }
  };
  const row = (it, side) => (
    <div key={`${side}-${it.intentId}`} className="rounded-md border border-indigo-200 bg-indigo-50/40 px-3 py-2 text-xs"
      data-testid={`intent-row-${side}-${it.intentId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-indigo-900">{side === 'sign' ? 'Waiting for your signature' : 'Plan'}:</span>
        <span className="text-gray-800">{it.summary || 'a change'}</span>
        <span className="text-gray-500">· {targetLine(it.target)}</span>
        {side === 'sign' && it.subscriberName ? <span className="text-gray-500">· from {it.subscriberName}</span> : null}
        <span className="ml-auto flex items-center gap-2">
          {(it.approvers || []).map((r) => <Sig key={r} role={r} it={it} />)}
        </span>
      </div>
      {it.previewNeeded && !previewReady(it) ? (
        <p className="mt-1 inline-flex items-center gap-1 text-gray-600" data-testid={`intent-preparing-${it.intentId}`}>
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />Preparing the change — you can sign once it's shown here.</p>
      ) : null}
      {it.previewNeeded && previewReady(it) ? (
        <ul className="mt-1 space-y-0.5 text-[11px] text-gray-700" data-testid={`intent-changes-${it.intentId}`}>
          {(it.preview?.changes || []).slice(0, 20).map((c, i) => (
            <li key={`${c.logicalId}-${i}`}>
              <span className="font-medium">{c.action}</span> {c.logicalId} <span className="text-gray-500">({c.type})</span>
              {c.replacement && !['False', 'false', ''].includes(c.replacement)
                ? <span className="ml-1 text-rose-600">replaces it ({c.replacement})</span> : null}
            </li>
          ))}
          {((it.preview?.changes || []).length > 20 || it.preview?.more)
            ? <li className="text-gray-500">…and {(it.preview?.changes || []).length - 20 + (it.preview?.more || 0)} more</li> : null}
          {!(it.preview?.changes || []).length ? <li className="text-gray-500">No resource changes listed.</li> : null}
        </ul>
      ) : null}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <button type="button" className={`${btn} inline-flex items-center gap-1 border border-gray-200 text-gray-700 hover:bg-gray-50`}
          data-testid={`intent-read-${it.intentId}`}
          onClick={() => read(it, side === 'sign' ? { action: 'artifact', tenantId: it.tenantId, networkId: it.networkId, intentId: it.intentId }
            : { action: 'artifact', intentId: it.intentId })}>
          <FileCode2 className="h-3 w-3" aria-hidden />{plan[it.intentId] !== undefined ? 'Hide the plan' : 'View the plan'}
        </button>
        {side === 'sign' && previewReady(it) ? (
          <>
            <button type="button" disabled={!!busy} data-testid={`intent-sign-${it.intentId}`}
              className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              onClick={() => act(`s:${it.intentId}`, { action: 'signExpert', tenantId: it.tenantId, networkId: it.networkId, intentId: it.intentId },
                { title: 'Sign this plan?', confirmLabel: 'Sign', message: `Your signature lets ${it.subscriberName || 'the room'} apply exactly this template (${targetLine(it.target)}) once its owner consents.` })}>
              {busy === `s:${it.intentId}` ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : 'Sign…'}
            </button>
            <button type="button" disabled={!!busy} data-testid={`intent-deny-${it.intentId}`}
              className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
              onClick={() => act(`d:${it.intentId}`, { action: 'deny', tenantId: it.tenantId, networkId: it.networkId, intentId: it.intentId })}>Decline</button>
          </>
        ) : null}
        {side === 'room' && it.status === 'pending' && previewReady(it) && (it.waitingFor || []).includes('owner') && deployIntents?.canConsent ? (
          <>
            <button type="button" disabled={!!busy} data-testid={`intent-consent-${it.intentId}`}
              className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              onClick={() => act(`c:${it.intentId}`, { action: 'sign', intentId: it.intentId },
                { title: 'Consent to this change?', confirmLabel: 'Consent', danger: true, message: `Exactly this template will be applied to ${targetLine(it.target)} — once, and only with the expert's signature too.` })}>
              {busy === `c:${it.intentId}` ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : 'Consent…'}
            </button>
            <button type="button" disabled={!!busy} data-testid={`intent-decline-${it.intentId}`}
              className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
              onClick={() => act(`x:${it.intentId}`, { action: 'deny', intentId: it.intentId })}>Decline</button>
          </>
        ) : null}
        {side === 'room' && it.status === 'approved' && deployIntents?.canPropose ? (
          <button type="button" disabled={!!busy} data-testid={`intent-run-${it.intentId}`}
            className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50`}
            onClick={() => act(`r:${it.intentId}`, { action: 'run', intentId: it.intentId },
              { title: 'Run the approved plan?', confirmLabel: 'Run', message: `It applies to ${targetLine(it.target)} once; the outcome is posted here.` })}>
            {busy === `r:${it.intentId}` ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : 'Run'}
          </button>
        ) : null}
        {side === 'room' && ['deploying', 'starting'].includes(it.status) ? (
          <span className="inline-flex items-center gap-1 text-gray-600" data-testid={`intent-running-${it.intentId}`}>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />Running…</span>
        ) : null}
      </div>
      {plan[it.intentId] !== undefined ? (
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-white p-2 text-[11px] text-gray-800" data-testid={`intent-plan-${it.intentId}`}>
          {plan[it.intentId]}</pre>
      ) : null}
    </div>
  );
  return (
    <div className="mb-2 space-y-1.5" data-testid="deploy-intents-banner">
      {toSign.map((it) => row(it, 'sign'))}
      {room.map((it) => row(it, 'room'))}
      {err ? <p className="text-[11px] text-rose-600" data-testid="deploy-intents-error">{err}</p> : null}
    </div>
  );
}
